import { ProductOption, ProductVariant, Product } from '@commerce/types/product'

type variantSettings = {
  metal: string
  diam_type: string
  tcw: string
  size: string
}

const variantsFilteredForCarat = (
  variants: ProductOption[][],
  variantSettings: {
    metal: string
    diam_type: string
    tcw: string
    size: string
  },
  currentFilters: string[]
) => {
  const filteredByMetal = variants.filter((vrn) => {
    const vn = vrn.filter((v) => v.displayName === 'Metal')[0]
    return vn ? vn.values[0].label === variantSettings.metal : false
  })

  return currentFilters.includes('diam_type')
    ? filteredByMetal.filter((vrn) => {
        const vn = vrn.filter((v) => v.displayName === 'Diamond Type')[0]
        return vn ? vn.values[0].label === variantSettings.diam_type : false
      })
    : filteredByMetal
}

const variantsFilteredForSize = (
  variants: ProductOption[][],
  variantSettings: {
    metal: string
    diam_type: string
    tcw: string
    size: string
  },
  currentFilters: string[]
) => {
  const filteredByMetal = variants.filter((vrn) => {
    const vn = vrn.filter((v) => v.displayName === 'Metal')[0]
    return vn ? vn.values[0].label === variantSettings.metal : false
  })

  const filteredByDiamType = currentFilters.includes('diam_type')
    ? filteredByMetal.filter((vrn) => {
        const vn = vrn.filter((v) => v.displayName === 'Diamond Type')[0]
        return vn ? vn.values[0].label === variantSettings.diam_type : false
      })
    : filteredByMetal

  return currentFilters.includes('tcw')
    ? filteredByDiamType.filter((vrn) => {
        const vn = vrn.filter((v) => v.displayName === 'Total Carat Weight')[0]
        return vn ? vn.values[0].label === variantSettings.tcw : false
      })
    : filteredByDiamType
}

const variantsFilteredForDiamType = (
  variants: ProductOption[][],
  variantSettings: {
    metal: string
    diam_type: string
    tcw: string
    size: string
  },
  currentFilters?: string[]
) => {
  return variants.filter((vrn) => {
    const vn = vrn.filter((v) => v.displayName === 'Metal')[0]
    return vn ? vn.values[0].label === variantSettings.metal : false
  })
}

const formatOption = (options: ProductOption[], type: string) => {
  return options.filter(
    (opt: ProductOption) =>
      (opt.displayName.includes('Carat')
        ? 'TCW'
        : opt.displayName.toUpperCase().split(' ').join('_')) === type
  )[0]
}

const getAllVariants = (product: Product, currentMetal: string) => {
  return product?.variants
    ?.filter((vrn) => {
      const vn = vrn.options.filter((v) => v.displayName === 'Metal')[0]
      return vn ? vn.values[0].label === currentMetal : false
    })
    .map((vr: ProductVariant) => vr.options)
}

const getAllAvailableFilters = (allVariants: ProductOption[][]) => {
  const metal =
    allVariants.filter((vrn) => vrn.filter((v) => v.displayName === 'Metal')[0])
      .length > 0
      ? 'metal'
      : ''
  const diamType =
    allVariants.filter(
      (vrn) => vrn.filter((v) => v.displayName === 'Diamond Type')[0]
    ).length > 0
      ? 'diam_type'
      : ''
  const ctw =
    allVariants.filter(
      (vrn) => vrn.filter((v) => v.displayName === 'Total Carat Weight')[0]
    ).length > 0
      ? 'tcw'
      : ''
  const size =
    allVariants.filter((vrn) => vrn.filter((v) => v.displayName === 'Size')[0])
      .length > 0
      ? 'size'
      : ''
  return [metal, diamType, ctw, size].filter((f) => f !== '')
}

const getVariantsFor = (
  opType: 'diam_type' | 'size' | 'tcw',
  allVariants: ProductOption[][],
  variantSettings: variantSettings,
  currentFilters: string[]
) => {
  return opType === 'diam_type'
    ? filteredBy(
        opType,
        variantsFilteredForDiamType(allVariants, variantSettings)
      )
    : opType === 'tcw'
    ? filteredBy(
        opType,
        variantsFilteredForCarat(allVariants, variantSettings, currentFilters)
      )
    : opType === 'size'
    ? filteredBy(
        opType,
        variantsFilteredForSize(allVariants, variantSettings, currentFilters)
      )
    : []
}

const filteredBy = (filter: string, variantFor: ProductOption[][]) => {
  return filter === 'diam_type'
    ? Array.from(
        new Set(
          variantFor
            .map((vnt) => {
              const vn = vnt.filter((v) => v.displayName === 'Diamond Type')[0]
              return vn ? vn.values[0].label : ''
            })
            .filter((v) => v !== '')
        )
      )
    : filter === 'tcw'
    ? Array.from(
        new Set(
          variantFor
            .map((vnt) => {
              const vn = vnt.filter(
                (v) => v.displayName === 'Total Carat Weight'
              )[0]
              return vn ? vn.values[0].label : ''
            })
            .filter((v) => v !== '')
        )
      )
    : filter === 'size'
    ? Array.from(
        new Set(
          variantFor
            .map((vnt) => {
              const vn = vnt.filter((v) => v.displayName === 'Size')[0]
              return vn ? vn.values[0].label : ''
            })
            .filter((v) => v !== '')
        )
      )
    : []
}

export {
  formatOption,
  getAllVariants,
  getAllAvailableFilters,
  getVariantsFor,
  filteredBy,
  variantsFilteredForCarat,
  variantsFilteredForSize,
  variantsFilteredForDiamType,
}
